import React, { useState, useEffect } from "react";
import Header from "./Shared/Header";
import Footer from "./Shared/Footer";
import MainBanner from "./Home/MainBanner";
import Services from "./Home/Services";
import FunFacts from "./Home/FunFacts";
import OurTeachers from "./Home/OurTeachers";
import BestCourses from "./Home/BestCourses";
import RegisterNow from "./Home/RegisterNow";
import LiveEvent from "./Home/LiveEvent";
import ContactUs from "./Home/ContactUs";
import TeacherDocumentation from "./Home/TeacherDocumentation";
import ApiServices from "./Services/ApiServices";
import { extractHost, getFullFileUrl } from "../Helpers/UrlHelpers";
import { useNavigate } from "react-router-dom";
import EMt7anyLoader from "./Shared/EMt7anyLoader";
import AdsVideoViewer from "./Shared/AdsVideoViewer";
import "owl.carousel/dist/assets/owl.carousel.css";
import "../assets/css/style.css";
import { storageHelper } from '../Helpers/storageHelper';
import GlobalRafiqChatBot from "./Shared/GlobalRafiqChatBot";

// Helper function to check if URL is a video
const isVideoUrl = (url) => {
  if (typeof url !== 'string') return false;
  const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.m4v', '.3gp'];
  return videoExtensions.some(ext => cleanUrl.endsWith(ext));
};


const Home = () => {
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiHost, setApiHost] = useState("");
  const [teacherAds, setTeacherAds] = useState([]);
  const [videoAd, setVideoAd] = useState(null);
  const [showVideoAd, setShowVideoAd] = useState(false);
  const isAuthenticated = !!storageHelper.getItem("token");
  const navigate = useNavigate();


  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await ApiServices.get("Home");
        setHomeData(response.data);
        setApiHost(extractHost(response.dynamic_data));

        // Extract and separate image and video ads from response
        if (response.data.items.ads && Array.isArray(response.data.items.ads)) {
          const allAds = response.data.items.ads;
          // Separate video ads and image ads
          const videoAds = allAds.filter(ad => ad.image && isVideoUrl(ad.image));
          const imageAds = allAds.filter(ad => ad.image && !isVideoUrl(ad.image));

          // Set image ads for MainBanner
          setTeacherAds(imageAds);

          // If there are video ads, select one randomly and show it after 3 seconds
          if (videoAds.length > 0) {
            const randomVideoAd = videoAds[Math.floor(Math.random() * videoAds.length)];
            setVideoAd(randomVideoAd);

            // Show video ad after 3 seconds
            setTimeout(() => {
              setShowVideoAd(true);
            }, 3000);
          }
        }
        setLoading(false);
      } catch (error) {

        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  if (loading) {
    return <EMt7anyLoader text="جاري تحميل الصفحة الرئيسية..." />;
  }

  return (
    <>
      <Header />
      <main className="home-modern">
        <MainBanner
          teacherAds={teacherAds}
          studentCount={homeData?.items?.student_count || 5000}
          teacherCount={homeData?.items?.teacher_count || 100}
        />

        {/* Video Ad Viewer */}
        {showVideoAd && videoAd && (
          <AdsVideoViewer
            videoUrl={getFullFileUrl(videoAd.image)}
            adsBadge="إعلان"
            adTitle={videoAd.title || "إعلان من منصة امتحاني"}
            adDescription={videoAd.description || "اكتشف المزيد من الخدمات التعليمية المميزة"}
            brandName={videoAd.teacher_name || "منصة امتحاني"}
            ctaText="اعرف المزيد"
            onCtaClick={() => {
              if (videoAd.link) {
                const hasProtocol = videoAd.link.startsWith("http://") || videoAd.link.startsWith("https://");
                const url = hasProtocol ? videoAd.link : `https://${videoAd.link}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }
              setShowVideoAd(false);
            }}
            skipAfter={5}
            allowSkip={true}
            allowClose={false}
            onClose={() => setShowVideoAd(false)}
            overlay={true}
            position="center"
            size="large"
            showBranding={true}
            showCta={false}
            autoPlay={true}
            onAdSkip={() => setShowVideoAd(false)}
            useWatchedCache={true}
          />
        )}

        <section className="home-shell shell-services">
          <Services />
        </section>

        <section className="home-shell shell-facts">
          <FunFacts
            student_count={homeData?.items?.student_count || 0}
            teacher_count={homeData?.items?.teacher_count || 0}
            executed_exams={homeData?.items?.executed_exams || 0}
            blog_count={homeData?.items?.blog_count || 0}
          />
        </section>

        <section className="home-shell shell-teachers">
          <OurTeachers
            best_teachers={homeData?.items?.best_teachers || []}
            apiHost={apiHost}
          />
        </section>

        <section className="home-shell shell-courses">
          <BestCourses
            best_courses={homeData?.items?.best_courses || []}
            apiHost={apiHost}
          />
        </section>

        <section className="home-shell shell-events">
          <LiveEvent />
        </section>

        {!isAuthenticated && (
          <section className="home-shell shell-register">
            <RegisterNow />
          </section>
        )}

        <section className="home-shell shell-contact">
          <ContactUs />
        </section>
      </main>
      <Footer />
      <GlobalRafiqChatBot />

      <style jsx>{`
        .home-modern {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(circle at 12% 8%, rgba(124, 58, 237, 0.08) 0, transparent 36%),
            radial-gradient(circle at 90% 22%, rgba(37, 99, 235, 0.07) 0, transparent 40%),
            linear-gradient(180deg, #f8faff 0%, #f6f8fc 45%, #f9fafc 100%);
        }

        .home-shell {
          position: relative;
        }

        .home-shell::before {
          content: "";
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: min(92vw, 1300px);
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.35), transparent);
          pointer-events: none;
        }

        .shell-services::before,
        .shell-register::before {
          display: none;
        }

        }
      `}</style>
    </>
  );
};

export default Home;
